<!--
  - Copyright (c) 2023-2024. Haodong JU
  -->

<template>
  <div id="pagePrint">
    <div>
      <page-header>
        {{ $t('inventoryStatus') }}
        <template #subtitle>
          {{ $t('chooseOneRecordToCheck') }}
        </template>
      </page-header>
      <div>
        <horizontal-list class="mt-4">
          <v-card
            :color="activeRecordId===-1?'amber lighten-4':'grey lighten-5'"
            class="pa-3"
            elevation="0"
            @click="activeRecordId=-1"
          >
            <div class="text-body-1 font-weight-black">
              {{ $t('fromLastCheckToNow') }}
            </div>
          </v-card>
          <v-card
            v-for="r in checkRecords"
            :key="r.id"
            :color="activeRecordId===r.id?'amber lighten-4':'grey lighten-5'"
            class="pa-3 d-flex flex-column"
            elevation="0"
            width="152"
            @click="activeRecordId=r.id"
          >
            <div class="text-body-1 font-weight-bold">
              {{ r.currentRecordTime |beautifulTime }}
            </div>
            <div class="text-body-2">
              {{ $t('inventoryRecord') }}#{{ r.id }}
            </div>
          </v-card>
        </horizontal-list>
      </div>
      <loading-provider :loading="loading">
        <div class="d-flex align-baseline">
          <div>
            <page-sub-header>
              {{ $t('inventoryDetail') }}
              <template #subtitle>
                {{ activeTimeRange[0] | beautifulTime }} ~ {{ activeTimeRange[1] | beautifulTime }}
              </template>
            </page-sub-header>
          </div>
        </div>

        <div
          :style="$vuetify.breakpoint.lgAndUp ? 'grid-template-columns: repeat(4,minmax(0,1fr));' : 'grid-template-columns: repeat(1,minmax(0,1fr))'"
          class="mb-4"
          style="display: grid;grid-gap: 8px;"
        >
          <v-card
            class="pa-4 d-flex flex-column"
            elevation="0"
          >
            <div class="text-body-1 font-weight-black">
              {{ $t('thisInventoryTotal') }}
            </div>
            <div class="text-body-2">
              {{ $t('alreadyCheckDifferentCost') }}
            </div>
            <v-spacer />
            <div class="text-h3 font-weight-black mt-4">
              {{ statistic.endOfTermCost | priceDisplay }}
            </div>
          </v-card>
          <v-card
            class="pa-4 d-flex flex-column"
            elevation="0"
          >
            <div class="text-body-1 font-weight-black">
              {{ $t('duringChange') }}
            </div>
            <div class="text-body-2">
              {{ $t('inTheEnd') }}({{ statistic.endOfTermCost | priceDisplay }}) - {{ $t('inTheEarly') }}({{
                statistic.beginningOfTermCost | priceDisplay
              }})
            </div>
            <v-spacer />
            <div class="text-h3 font-weight-black mt-4">
              {{ statistic.endOfTermCost - statistic.beginningOfTermCost | priceDisplay }}
            </div>
          </v-card>
          <v-card
            class="pa-4 d-flex flex-column"
            elevation="0"
          >
            <div class="text-body-1 font-weight-black">
              {{ $t('soldCost') }}
            </div>
            <div class="text-body-2">
              {{ $t('outRawMaterialsPriceDuringThisPeriod') }}
            </div>
            <v-spacer />
            <div class="text-h3 font-weight-black mt-4">
              {{ statistic.totalOutDishResourceCost * -1 | priceDisplay }}({{ statistic.loss | priceDisplay }})
            </div>
          </v-card>
          <v-card
            class="pa-4 d-flex flex-column"
            elevation="0"
          >
            <div class="text-body-1 font-weight-black">
              {{ $t('GP') }}
            </div>
            <div class="text-body-2">
              {{ $t('periodTurnover') }}({{ totalSales | priceDisplay }}) - {{ $t('soldCost') }}
            </div>
            <v-spacer />
            <div class="text-h3 font-weight-black">
              {{ totalSales + statistic.totalOutDishResourceCost | priceDisplay }}
              ({{
                clamp(((totalSales + statistic.totalOutDishResourceCost) * 100 / (totalSales || 1)), 0, 100).toFixed(2)
              }}%)
            </div>
          </v-card>
        </div>
        <div style="position: sticky;top: 0;z-index: 2;background: rgb(250, 251, 252)">
          <div class="mb-4">
            <v-tabs
              v-model="currentTab"
              color="success darken-4"
              show-arrows
            >
              <v-tab>{{ $t('checkStatus') }}</v-tab>
              <v-tab>{{ $t('totalChange') }}</v-tab>
              <v-tab>{{ $t('changeRecord') }}</v-tab>
            </v-tabs>
            <v-divider />
          </div>
        </div>
        <div>
          <div>
            <template v-if="currentTab===0">
              <template v-if="resourceCount.length > 0">
                <v-card
                  v-for="r in resourceCount"
                  :key="r.dishResource.id"
                  class="pa-4 mb-1 "
                  elevation="0"
                >
                  <div class="d-flex align-center">
                    <div>
                      {{ r.dishResource.name }}
                    </div>
                    <v-spacer />
                    <div style="text-align: right">
                      {{ r.unitDisplay }}
                    </div>
                  </div>
                  <div class="py-2">
                    <div
                      v-for="l in r.groupByPrice"
                      :key="l.price"
                      class="d-flex align-center text-body-2"
                    >
                      {{ l.price*l.amount | priceDisplay }}
                      <v-spacer />
                      {{ l.unitDisplay || 0 }}
                    </div>
                  </div>
                </v-card>
              </template>
              <template v-else>
                <div
                  class="d-flex align-center justify-center flex-column"
                  style="height: 400px;width: 100%"
                >
                  <v-icon size="64">
                    mdi-tea
                  </v-icon>
                  <div>
                    {{ $t('thisCheckHaveNotChange') }}
                  </div>
                </div>
              </template>
            </template>
            <template v-if="currentTab===1">
              <div
                v-if="recordList.length>0"
                class="pa-1 mt-4"
              >
                <v-card
                  v-for="{dishResource,changeLogSummary} in recordList"

                  :key="dishResource.id"
                  class="pa-4 mb-1 d-flex align-center"
                  elevation="0"
                >
                  <div>
                    {{ dishResource.dishResource.name }}
                  </div>
                  <v-spacer />
                  <div class="d-flex mr-8">
                    <v-icon>
                      mdi-cash-multiple
                    </v-icon>
                    <div style="width: 96px;text-align: right">
                      {{ changeLogSummary.totalCost | priceDisplay }}
                    </div>
                  </div>
                  <div class="d-flex">
                    <v-icon
                      v-if="changeLogSummary.amount<0"
                      color="warning"
                    >
                      mdi-trending-down
                    </v-icon>
                    <v-icon
                      v-if="changeLogSummary.amount>=0"
                      color="success"
                    >
                      mdi-trending-up
                    </v-icon>
                    <div
                      class="text-no-wrap ml-2"
                      style="min-width: 72px;text-align: right"
                    >
                      {{ changeLogSummary.unitDisplay }}
                    </div>
                  </div>
                </v-card>
              </div>
              <div v-else>
                <div
                  class="d-flex align-center justify-center flex-column"
                  style="height: 400px;width: 100%"
                >
                  <v-icon size="64">
                    mdi-tea
                  </v-icon>
                  <div>
                    {{ $t('thisCheckHaveNotChange') }}
                  </div>
                </div>
              </div>
            </template>
            <template v-if="currentTab===2">
              <div
                v-if="detailedLog.length>0"
                class="pa-1 mt-4"
              >
                <v-card
                  v-for="{changeLog,dishResource} in detailedLog"
                  :key="changeLog.id"
                  class="pa-4 mb-1 "
                  elevation="0"
                >
                  <div class="d-flex align-center">
                    <v-chip
                      :color="changeLog.operation==='Enter'?'success darken-4':'error darken-4'"
                      label
                      small
                    >
                      {{ changeLog.operation }}
                    </v-chip>
                    <div class="text-body-1 mx-2 font-weight-bold">
                      {{ dishResource.dishResource.name }}
                    </div>
                    <v-spacer />
                    <div class="d-flex">
                      <v-icon
                        v-if="changeLog.amount<0"
                        color="warning"
                      >
                        mdi-trending-down
                      </v-icon>
                      <v-icon
                        v-else
                        color="success"
                      >
                        mdi-trending-up
                      </v-icon>
                      <div style="width: 96px;text-align: right">
                        {{ changeLog.unitDisplay }}
                      </div>
                    </div>
                  </div>
                  <div class="d-flex align-center">
                    <div>
                      <div class="text-body-1">
                        {{ changeLog.note || $t('withoutNote') }}
                      </div>
                    </div>
                    <v-spacer />

                    <div>
                      <div class="d-flex text-body-1 align-center">
                        <v-icon>
                          mdi-cash-multiple
                        </v-icon>
                        <div style="width: 96px;text-align: right">
                          {{ changeLog.costLiteral | priceDisplay }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style="width: 96px">
                    <div class="text-caption">
                      {{ changeLog.createdAt | beautifulTime }}
                    </div>
                  </div>
                </v-card>
              </div>
              <div v-else>
                <div
                  class="d-flex align-center justify-center flex-column"
                  style="height: 400px;width: 100%"
                >
                  <v-icon size="64">
                    mdi-tea
                  </v-icon>
                  <div>
                    {{ $t('thisCheckHaveNotChange') }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </loading-provider>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/views/widget/typography/PageHeader.vue'
import { clamp } from 'lodash-es'
import {
  getClearingSummary,
  getClearList,
  getInOutLog,
  getResourceCount,
  getStatistic
} from '@/model/dataLayer/api/storage/inventory'
import HorizontalList from '@/views/widget/uiComponent/HorizontalList.vue'
import PageSubHeader from '@/views/widget/typography/PageSubHeader.vue'
import dayjs from 'dayjs'
import { previewZBonByTimeSpan } from '@/model/dataLayer/service/umsatzInfo'
import LoadingProvider from '@/views/widget/LoadingProvider'

const ktorTimeFormat = 'YYYY-MM-DDTHH:mm:ss'

export default {
  name: 'InventoryPage',
  components: {
    LoadingProvider,
    PageSubHeader,
    HorizontalList,
    PageHeader
  },
  data () {
    return {
      checkRecords: [],
      activeRecordId: null,
      recordList: [],
      resourceCount: [],
      detailedLog: [],
      currentTab: 0,
      statistic: {
        totalCost: 0,
        totalEnterDishResourceCost: 0,
        totalOutDishResourceCost: 0,
        beginningOfTermCost: 0,
        endOfTermCost: 0,
        loss: 0
      },
      totalSales: 0,
      loading: false
    }
  },
  computed: {
    activeTimeRange () {
      const record = this.checkRecords.find(it => it.id === this.activeRecordId)
      if (record) {
        return [record.previousRecordTime, record.currentRecordTime]
      } else {
        return [
          this.checkRecords?.[0]?.currentRecordTime ??
            dayjs().subtract(2, 'd').format(ktorTimeFormat),
          dayjs().format(ktorTimeFormat)
        ]
      }
    }
  },
  watch: {
    activeRecordId () {
      this.refreshData()
    }
  },
  async mounted () {
    await this.refreshCheckRecord()
  },
  methods: {
    clamp,
    async refreshCheckRecord () {
      console.log(await getClearList(), 'list')
      this.checkRecords = await getClearList()
      this.activeRecordId = -1
    },
    async refreshData () {
      this.loading = true
      await this.refreshSummaries()
      await this.refreshResource()
      this.detailedLog = await getInOutLog(...this.activeTimeRange)
      this.statistic = await getStatistic(...this.activeTimeRange)
      this.totalSales = (await previewZBonByTimeSpan(...this.activeTimeRange.map(it => {
        return dayjs(it).format('YYYY-MM-DD HH:mm:ss')
      }))).content.total
      this.loading = false
    },
    async refreshResource () {
      this.resourceCount = (await getResourceCount(this.activeRecordId)).map(it => {
        if (!it.unitDisplay) {
          it.unitDisplay = 0
        }
        if (it.currentAmount < 0) {
          it.unitDisplay = '-' + it.unitDisplay
        }
        it.groupByPrice.forEach(x => {
          if (x.amount < 0) {
            x.unitDisplay = '-' + x.unitDisplay
          }
        })
        return it
      })
    },
    async refreshSummaries () {
      this.recordList = await getClearingSummary(...this.activeTimeRange)
    }
  }
}
</script>

<style scoped>

</style>
